import { type ReactNode, useState, useCallback } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Handshake,
  HeadphonesIcon,
  Database,
  Activity,
  Menu,
  PlusCircle,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent } from "@/components/ui/sheet";

interface NavItem {
  readonly label: string;
  readonly href: string;
  readonly icon: ReactNode;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    label: "דשבורד",
    href: "/super/dashboard",
    icon: <LayoutDashboard className="h-4 w-4" />,
  },
  {
    label: "דיירים",
    href: "/super/tenants",
    icon: <Users className="h-4 w-4" />,
  },
  {
    label: "הקמת דייר",
    href: "/super/provisioning",
    icon: <PlusCircle className="h-4 w-4" />,
  },
  {
    label: "חיוב",
    href: "/super/billing",
    icon: <CreditCard className="h-4 w-4" />,
  },
  {
    label: "שותפים",
    href: "/super/partners",
    icon: <Handshake className="h-4 w-4" />,
  },
  {
    label: "בריאות",
    href: "/super/health",
    icon: <Activity className="h-4 w-4" />,
  },
  {
    label: "תמיכה",
    href: "/super/support",
    icon: <HeadphonesIcon className="h-4 w-4" />,
  },
  {
    label: "גיבויים",
    href: "/super/backups",
    icon: <Database className="h-4 w-4" />,
  },
];

// ---------------------------------------------------------------------------
// Individual nav link
// ---------------------------------------------------------------------------
interface NavLinkItemProps {
  readonly item: NavItem;
  readonly isActive: boolean;
}

function NavLinkItem({ item, isActive }: NavLinkItemProps): ReactNode {
  return (
    <NavLink
      to={item.href}
      className={cn(
        "relative flex min-h-[44px] items-center gap-3 rounded-lg mx-2 px-3 py-2 text-sm font-medium",
        "transition-all duration-150 group",
        isActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
          : "text-sidebar-foreground hover:bg-sidebar-accent/40 hover:text-sidebar-accent-foreground",
      )}
    >
      {/* Active left-edge indicator */}
      {isActive && (
        <span className="absolute inset-y-1 start-0 w-0.5 rounded-full bg-sidebar-primary" />
      )}

      {/* Icon container */}
      <span
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150",
          isActive
            ? "bg-sidebar-primary/20 text-sidebar-primary"
            : "bg-sidebar-accent/0 text-sidebar-foreground/60 group-hover:bg-sidebar-accent/50 group-hover:text-sidebar-accent-foreground",
        )}
      >
        {item.icon}
      </span>

      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// Sidebar content (shared between desktop + mobile sheet)
// ---------------------------------------------------------------------------
function SidebarNav(): ReactNode {
  const location = useLocation();

  return (
    <nav
      className="flex h-full flex-col bg-sidebar-background text-sidebar-foreground"
      dir="rtl"
    >
      {/* ── Logo / brand area ── */}
      <div
        className={[
          "relative flex h-16 items-center gap-3 px-4 shrink-0",
          "border-b border-sidebar-border/70",
          "overflow-hidden",
        ].join(" ")}
      >
        {/* Gradient glow behind logo */}
        <div className="absolute inset-0 bg-gradient-to-l from-sidebar-primary/8 via-transparent to-transparent pointer-events-none" />

        {/* Shield icon */}
        <div
          className={[
            "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
            "bg-gradient-to-br from-destructive/80 to-destructive",
            "shadow-sm shadow-destructive/30",
          ].join(" ")}
        >
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>

        {/* Title */}
        <div className="relative min-w-0 flex-1">
          <p className="text-sm font-bold text-sidebar-primary leading-tight truncate">
            ניהול פלטפורמה
          </p>
          <p className="text-[11px] text-muted-foreground leading-tight">
            Super Admin Console
          </p>
        </div>

        {/* SA badge */}
        <Badge
          variant="destructive"
          className="relative text-[10px] px-1.5 py-0 shrink-0 font-bold"
        >
          SA
        </Badge>
      </div>

      {/* ── Nav items ── */}
      <div className="flex-1 overflow-y-auto py-3 sidebar-scroll">
        <div className="space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            return <NavLinkItem key={item.href} item={item} isActive={isActive} />;
          })}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="shrink-0 px-4 py-3 border-t border-sidebar-border/60">
        <Separator className="mb-3 opacity-0" />
        <p className="text-[11px] text-muted-foreground/60 text-center tracking-wide">
          Foxy Platform v1.0
        </p>
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export function SuperAdminLayout(): ReactNode {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Derive current page title from pathname for the header breadcrumb
  const currentNav = NAV_ITEMS.find((item) =>
    location.pathname.startsWith(item.href),
  );

  const handleMenuClick = useCallback(() => {
    setMobileOpen(true);
  }, []);

  // Current time greeting
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "בוקר טוב" : hour < 17 ? "צהריים טובים" : "ערב טוב";

  return (
    <div dir="rtl" className="flex min-h-dvh bg-background">
      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:block shrink-0 border-e border-sidebar-border/70">
        <div className="sticky top-0 h-dvh w-[260px] overflow-hidden">
          <SidebarNav />
        </div>
      </aside>

      {/* ── Mobile sidebar (Sheet) ── */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="right" className="w-[260px] p-0">
          <SidebarNav />
        </SheetContent>
      </Sheet>

      {/* ── Main content area ── */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* ── Header ── */}
        <header
          className={[
            "relative flex h-14 shrink-0 items-center gap-3 px-4",
            "bg-background/80 backdrop-blur-sm",
            "border-b border-border/50",
            "z-10",
          ].join(" ")}
        >
          {/* Gradient bottom edge */}
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-l from-primary/20 via-border/60 to-transparent" />

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={handleMenuClick}
            className={[
              "md:hidden flex items-center justify-center",
              "min-h-[44px] min-w-[44px] rounded-lg",
              "hover:bg-muted transition-colors duration-150",
              "text-muted-foreground hover:text-foreground",
            ].join(" ")}
            aria-label="פתח תפריט"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Page title / breadcrumb */}
          {currentNav && (
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-muted-foreground/60">פלטפורמה</span>
              <span className="text-muted-foreground/40">/</span>
              <span className="font-medium text-foreground">{currentNav.label}</span>
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side: greeting + badge */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">
              {greeting}, <span className="font-medium text-foreground">אליהו</span>
            </span>

            <div className="flex items-center gap-1.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-destructive/10">
                <ShieldCheck className="h-4 w-4 text-destructive" />
              </div>
              <Badge
                variant="destructive"
                className="text-xs gap-1.5 font-semibold"
              >
                Super Admin
              </Badge>
            </div>
          </div>
        </header>

        {/* ── Page content ── */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
